import { type Collection, createLiveQueryCollection, eq, type NonSingleResult } from "@tanstack/db";

import { slugifyTagLabel } from "@storyboard/driver-query/tagColumns";
import { store } from "@/store/store";
import type { Tag } from "@/store/types";

type TagRecord = {
  id: string;
  /** Absent only on the AppSync `workspaceTags` event, whose publisher is currently disabled (SVS-2514). */
  slug?: string;
  label: string;
  enabled: boolean;
  multiValue: boolean;
  values?: string[];
};

function slimTag({ id, slug, label, enabled, multiValue }: TagRecord): Tag {
  // The poller feeds records through unchecked casts, and sibling tables have shipped
  // int-as-boolean flags before; normalize so the strict eq(enabled, true) live query holds.
  // The slug fallback mirrors the server's own slug derivation for a record that predates the field.
  return {
    id,
    slug: slug ?? slugifyTagLabel(label),
    label,
    enabled: Boolean(enabled),
    multiValue: Boolean(multiValue),
  };
}

export async function bulkUpsertTag(records: TagRecord[]) {
  if (records.length === 0) return;
  store.upsert("tag", records.map(slimTag));
}

export async function replaceAllTags(records: TagRecord[]) {
  store.batch(() => {
    store.clear("tag");
    store.upsert("tag", records.map(slimTag));
  });
}

/**
 * Enabled tags, maintained incrementally by the live-query engine. Disabled tags stay in the
 * collection to track soft-deletes across incremental syncs but never surface to consumers.
 */
// Annotated because the inferred live-query type references @tanstack/db internals that TS
// refuses to name in the emitted declaration (TS2742).
export const enabledTagsQuery: Collection<Tag> & NonSingleResult = createLiveQueryCollection({
  // Tags are tiny and drive the always-visible driver grid: compute from creation and keep the
  // result warm across subscriber churn, so re-entering the view never paints an empty frame.
  startSync: true,
  gcTime: 0,
  query: (q) => q.from({ tag: store.collection("tag") }).where(({ tag }) => eq(tag.enabled, true)),
});
